import React, { useState } from 'react';
import { Box } from '@mui/material';

const defaultSnapshot = '/mapicons/DefaultSnapshot.png';

export interface PassageImageProps {
  imageUrl: string;
  filename: string;
  description: string;
  onClick?: () => void;
  onClose?: () => void;
  clickable?: boolean;
  expanded?: boolean;
}

const PassageImage: React.FC<PassageImageProps> = ({
  filename,
  description,
  imageUrl,
  onClick,
  onClose,
  clickable = false,
  expanded,
}) => {
  const [hovered, setHovered] = useState(false);
  const [error, setError] = useState(false);
  const [timestamp, setTimestamp] = useState(Date.now());
  const canClose = Boolean(expanded && onClose);
  const canExpand = Boolean(!expanded && clickable && onClick);
  const isInteractive = canClose || canExpand;

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(Date.now());
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      position="relative"
      sx={{ display: 'inline-block', width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        component="img"
        src={
          (imageUrl || import.meta.env.VITE_REACT_APP_CAMERA_URL) +
          filename +
          '?x=' +
          timestamp
        }
        title={
          canClose
            ? 'Click to Close'
            : canExpand
            ? 'Click to Expand'
            : description
        }
        alt={!error && !isInteractive ? description : ''}
        aria-label={
          !error && isInteractive
            ? canClose
              ? `Close ${description} camera view`
              : canExpand
              ? `Expand ${description} camera view`
              : undefined
            : undefined
        }
        role={!error && isInteractive ? 'button' : undefined}
        tabIndex={!error && isInteractive ? 0 : undefined}
        aria-pressed={!error && canClose ? 'true' : 'false'}
        onKeyDown={(e) => {
          if (!error && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            if (canClose && onClose) {
              onClose();
            } else if (canExpand) {
              onClick?.();
            }
          }
        }}
        sx={{
          width: '100%',
          height: 'auto',
          borderRadius: 2,
          cursor: !error && isInteractive ? 'pointer' : 'default',
          objectFit: 'contain',
          transition: 'all 0.2s ease',
          boxShadow: hovered && !error && isInteractive ? 4 : undefined,
          filter:
            hovered && !error && isInteractive ? 'brightness(0.9)' : 'none',
          outline: 'none',
          ...(!error &&
            isInteractive && {
              '&:focus': {
                outline: '2px solid #1976d2',
                outlineOffset: '2px',
              },
            }),
        }}
        onError={(e: unknown) => {
          setError(true);
          const event = e as React.SyntheticEvent<HTMLImageElement, Event>;
          if (event?.target && event.target instanceof HTMLImageElement) {
            event.target.src = defaultSnapshot;
          }
        }}
      />
      {hovered && !error && isInteractive && (
        <Box
          role="status"
          aria-live="polite"
          onClick={
            !error
              ? canClose
                ? onClose
                : canExpand
                ? onClick
                : undefined
              : undefined
          }
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.2)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '1rem',
            zIndex: 2,
          }}
        >
          {canClose ? 'Click to Close' : canExpand ? 'Click to Expand' : ''}
        </Box>
      )}
    </Box>
  );
};

export default PassageImage;
